"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchConfirmationDialog } from "@/fetch-ui/FetchConfirmationDialog";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { AddRoomTypeMenu } from "@/features/event-details-rooms/_components/AddRoomTypeMenu";
import { ANCHORED_ROOM_TYPE } from "@/features/event-details/_logic/constants";
import {
  computeRoomNightsSummary,
  deriveVisibleTypes,
  getNightsBetween,
} from "@/features/event-details-rooms/_logic/rooms";
import { ROOM_TYPES, type RoomType } from "@/lib/utils/accommodations";
import { formatDateWithWeekday } from "@/lib/utils/formatting";
import { formatRoomCount, parseRoomCount } from "@/lib/utils/room-count";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formElementVariants, isBelowRoomMinimum } from "../../_logic/constants";
import { asRange } from "../../_logic/dates";
import {
  accommodationsPeakRoomCount,
  hasAccommodationsCount,
  hasAccommodationsDates,
} from "../../_logic/step-completion";
import { useSubPhase } from "../../_hooks/use-sub-phase";
import { GuestroomSubPhase, RoomTypeCounts, StepProps } from "../../_types/wizard-types";
import BelowMinRoomsModal from "../BelowMinRoomsModal";
import { SubPhasePanel } from "./SubPhasePanel";
import { YesNoGate } from "./YesNoGate";

const GUESTROOM_PHASES: readonly GuestroomSubPhase[] = ["gate", "count"];

/**
 * One night's row in the guestrooms grid. Memoized so a keystroke in one night's cell doesn't
 * re-render every other row — `counts` is that night's own slice of `detailed`, which keeps its
 * object identity across an edit to a different night (the parent only replaces the edited night's
 * key), and `onCellChange` is a stable callback, so unrelated rows correctly bail out.
 *
 * @param date - This row's night (YYYY-MM-DD).
 * @param index - Zero-based position among the stay's nights, for the "Night N" label.
 * @param counts - This night's per-room-type counts, or `undefined` if unset.
 * @param visibleColumns - Room types currently shown as columns.
 * @param onCellChange - Called with `(date, roomType, rawInputValue)` on any cell edit.
 */
const GuestroomRow = memo(function GuestroomRow({
  date,
  index,
  counts,
  visibleColumns,
  onCellChange,
}: {
  date: string;
  index: number;
  counts: RoomTypeCounts | undefined;
  visibleColumns: RoomType[];
  onCellChange: (date: string, roomType: RoomType, raw: string) => void;
}) {
  const total = ROOM_TYPES.reduce((sum, type) => sum + (counts?.[type] ?? 0), 0);
  const weekdayLabel = formatDateWithWeekday(date);

  return (
    <tr className="transition-colors hover:bg-indigo-50/30">
      <td className="px-5 py-3">
        <span className="block text-sm font-medium text-gray-900 whitespace-nowrap">{weekdayLabel}</span>
        <span className="block text-xs font-medium text-gray-400 whitespace-nowrap">Night {index + 1}</span>
      </td>
      {visibleColumns.map((type) => (
        <td key={type} className="px-3 py-2.5 align-middle">
          <div className="mx-auto w-16">
            <FetchInput
              type="number"
              hideStepper
              min={0}
              value={formatRoomCount(counts?.[type])}
              onChange={(e) => onCellChange(date, type, e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              aria-label={`${type} rooms for ${weekdayLabel} (night ${index + 1})`}
              className="text-center tabular-nums"
            />
          </div>
        </td>
      ))}
      <td className="px-3 py-2.5 text-center text-sm font-semibold tabular-nums text-gray-900">{total}</td>
    </tr>
  );
});

/**
 * Inline date × room-type grid — one row per night in the check-in/check-out range, one column per
 * visible room type (add/remove via `AddRoomTypeMenu`, matching the event-details Rooms section),
 * a sticky "quick add" row for filling the same count into every night at once, a read-only
 * nightly total, and a peak-rooms summary that stays visible regardless of scroll position.
 *
 * @param nights - The stay's nights (YYYY-MM-DD), from `getNightsBetween`.
 * @param detailed - Current per-night, per-room-type breakdown (missing nights default to all-zero).
 * @param visibleColumns - Room types currently shown as columns.
 * @param availableTypesForAdd - Room types not yet visible, offered by the "Add room type" menu.
 * @param numPeople - Expected headcount, used to seed the quick-add Single column's default value.
 * @param onChange - Called with the full updated breakdown on any cell edit.
 * @param onAddType - Called when the planner picks a type to add from the menu.
 * @param onRemoveType - Called when the planner removes a non-anchored type column.
 */
function GuestroomsGrid({
  nights,
  detailed,
  visibleColumns,
  availableTypesForAdd,
  numPeople,
  onChange,
  onAddType,
  onRemoveType,
}: {
  nights: string[];
  detailed: { [date: string]: RoomTypeCounts };
  visibleColumns: RoomType[];
  availableTypesForAdd: RoomType[];
  /** Expected headcount from the Headcount step — seeds the quick-add Single column so the common
   *  "one room per attendee" case is a single Apply click instead of a blank field. */
  numPeople: number;
  onChange: (next: { [date: string]: RoomTypeCounts }) => void;
  onAddType: (type: RoomType) => void;
  onRemoveType: (type: RoomType) => void;
}) {
  const [quickFill, setQuickFill] = useState<Partial<Record<RoomType, string>>>(() =>
    numPeople > 0 ? { [ANCHORED_ROOM_TYPE]: String(numPeople) } : {}
  );
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Read through a ref rather than closing over `detailed` directly, so the callback's identity
  // stays stable across edits — `detailed` changes every keystroke, and a callback that closed over
  // it would too, defeating `GuestroomRow`'s memoization on every row.
  const detailedRef = useRef(detailed);
  detailedRef.current = detailed;

  const handleCellChange = useCallback(
    (date: string, roomType: RoomType, raw: string) => {
      const count = parseRoomCount(raw);
      const current = detailedRef.current;
      onChange({ ...current, [date]: { ...current[date], [roomType]: count } });
    },
    [onChange]
  );

  // Applies each quick-fill column with a value to every night at once — e.g. "same 10 Single
  // rooms every night" without clicking into each row. Columns left blank are untouched, so
  // quick-filling Single doesn't clobber Double counts already entered per night.
  const handleQuickFillApply = () => {
    const next: { [date: string]: RoomTypeCounts } = { ...detailed };
    for (const date of nights) {
      const updated = { ...next[date] };
      for (const type of visibleColumns) {
        const raw = quickFill[type];
        if (raw === undefined || raw === "") continue;
        updated[type] = parseRoomCount(raw);
      }
      next[date] = updated;
    }
    onChange(next);
  };

  // Resets every night's counts across every column back to blank — a fast way to start the
  // breakdown over after a bad quick-add or a change of plans, without removing the room-type
  // columns themselves. Confirmed first since it can wipe a full week's worth of entries in one click.
  const handleClearTable = () => {
    onChange({});
    setShowClearConfirm(false);
  };

  const hasAnyValue = useMemo(
    () => nights.some((date) => visibleColumns.some((type) => (detailed[date]?.[type] ?? 0) > 0)),
    [nights, visibleColumns, detailed]
  );

  const { peakRooms, totalRoomNights } = useMemo(() => computeRoomNightsSummary(nights, detailed), [detailed, nights]);

  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Room breakdown</span>
        <div className="flex items-center gap-1">
          {hasAnyValue && (
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Clear table
            </button>
          )}
          <AddRoomTypeMenu availableTypes={availableTypesForAdd} onSelect={onAddType} />
          <FetchConfirmationDialog
            isOpen={showClearConfirm}
            onClose={() => setShowClearConfirm(false)}
            onConfirm={handleClearTable}
            title="Clear the room breakdown?"
            message="This removes every room count you've entered across all nights. This can't be undone."
            confirmText="Clear table"
            type="danger"
          />
        </div>
      </div>
      <div className="max-h-96 overflow-auto rounded-xl border border-gray-100 bg-white scrollbar-visible">
        <table className="w-full min-w-[560px] border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-gray-200">
              <th
                scope="col"
                className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Night
              </th>
              {visibleColumns.map((type) => (
                <th
                  key={type}
                  scope="col"
                  className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                >
                  <span className="relative inline-block">
                    {type}
                    {type !== ANCHORED_ROOM_TYPE ? (
                      <button
                        type="button"
                        onClick={() => onRemoveType(type)}
                        aria-label={`Hide ${type} column`}
                        className="absolute left-full top-1/2 ml-1 -translate-y-1/2 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-1"
                      >
                        <X className="size-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </span>
                </th>
              ))}
              <th
                scope="col"
                className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Total
              </th>
            </tr>
            <tr className="border-b border-gray-200 bg-indigo-50/40">
              <td className="px-5 py-2">
                <span className="text-xs font-medium text-indigo-500">Quick add — every night</span>
              </td>
              {visibleColumns.map((type) => (
                <td key={type} className="px-3 py-2 align-middle">
                  <div className="mx-auto w-16">
                    <FetchInput
                      type="number"
                      hideStepper
                      min={0}
                      placeholder="—"
                      value={quickFill[type] ?? ""}
                      onChange={(e) => setQuickFill((prev) => ({ ...prev, [type]: e.target.value }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      aria-label={`Quick-add ${type} rooms for every night`}
                      className="text-center tabular-nums"
                    />
                  </div>
                </td>
              ))}
              <td className="px-3 py-2 text-center">
                <button
                  type="button"
                  onClick={handleQuickFillApply}
                  aria-label="Apply quick-add values to every night"
                  className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-100"
                >
                  Apply
                </button>
              </td>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {nights.map((date, index) => (
              <GuestroomRow
                key={date}
                date={date}
                index={index}
                counts={detailed[date]}
                visibleColumns={visibleColumns}
                onCellChange={handleCellChange}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-indigo-50 px-4 py-3">
        <span className="text-sm font-medium text-indigo-700">Peak rooms</span>
        <span className="text-sm font-semibold tabular-nums text-indigo-900">
          {peakRooms.toLocaleString()} peak / {totalRoomNights.toLocaleString()} room nights
        </span>
      </div>
    </div>
  );
}

/** Step: Guestroom requirements — Yes/No gate, then a per-night/per-room-type breakdown grid. Dates
 *  are derived from the Dates step's preferred event date rather than asked again here. */
export const GuestroomsStep = ({ formData, dispatch, onNext, onBack }: StepProps) => {
  // Hooks
  const {
    phase: subPhase,
    direction: subDirection,
    goTo,
    goBack: handleSubBack,
  } = useSubPhase(GUESTROOM_PHASES, formData.accommodations.needed ? "count" : "gate", onBack);

  // States
  const [minRoomsModalReason, setMinRoomsModalReason] = useState<"count" | "northstar" | null>(null);
  const [visibleTypes, setVisibleTypes] = useState<Set<RoomType>>(() =>
    deriveVisibleTypes(formData.accommodationsDetailed ?? {})
  );

  // Derived values
  const { accommodations, accommodationsDetailed } = formData;
  const hasDates = hasAccommodationsDates(formData);
  const hasCount = hasAccommodationsCount(formData);
  // Northstar requests are sourced through our platform, so they must include guestrooms.
  const isNorthstar = !!formData.northstarRequestId;
  const nights = useMemo(
    () => (hasDates ? getNightsBetween(accommodations.checkIn!, accommodations.checkOut!) : []),
    [hasDates, accommodations.checkIn, accommodations.checkOut]
  );
  const visibleColumns = useMemo<RoomType[]>(() => Array.from(visibleTypes), [visibleTypes]);
  const availableTypesForAdd = useMemo<RoomType[]>(
    () => ROOM_TYPES.filter((t) => !visibleTypes.has(t)),
    [visibleTypes]
  );

  // Handlers
  const handleNo = useCallback(() => {
    if (isNorthstar) {
      setMinRoomsModalReason("northstar");
      return;
    }
    dispatch({ type: "SET_ACCOMMODATIONS_NEEDED", payload: false });
    onNext();
  }, [isNorthstar, dispatch, onNext]);

  const handleYes = useCallback(() => {
    dispatch({ type: "SET_ACCOMMODATIONS_NEEDED", payload: true });
    const preferredDates = asRange(formData.eventDates);
    if (preferredDates) {
      dispatch({
        type: "SET_ACCOMMODATION_DATES",
        payload: { checkIn: preferredDates.startDate, checkOut: preferredDates.endDate },
      });
    }
    goTo("count");
  }, [dispatch, goTo, formData.eventDates]);

  const handleDetailChange = useCallback(
    (next: { [date: string]: RoomTypeCounts }) => {
      dispatch({ type: "SET_ACCOMMODATIONS_DETAILED", payload: next });
    },
    [dispatch]
  );

  const handleAddType = useCallback((type: RoomType) => {
    setVisibleTypes((prev) => {
      if (prev.has(type)) return prev;
      const next = new Set(prev);
      next.add(type);
      return next;
    });
  }, []);

  const handleRemoveType = useCallback(
    (type: RoomType) => {
      if (type === ANCHORED_ROOM_TYPE) return;
      setVisibleTypes((prev) => {
        if (!prev.has(type)) return prev;
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
      // Zero the type's counts across all nights so removal persists on save and the column
      // stays hidden if the step remounts (derived visibility = no non-zero data left). Routed
      // through the same `handleDetailChange` path as a normal cell edit, not a second dispatch.
      handleDetailChange(
        Object.fromEntries(
          Object.entries(accommodationsDetailed ?? {}).map(([date, counts]) => [date, { ...counts, [type]: 0 }])
        )
      );
    },
    [handleDetailChange, accommodationsDetailed]
  );

  const handleCountContinue = useCallback(() => {
    const belowMinimum = isBelowRoomMinimum(accommodationsPeakRoomCount(formData));
    if (belowMinimum) {
      setMinRoomsModalReason("count");
      return;
    }
    onNext();
  }, [formData, onNext]);

  const handleContinueWithoutGuestrooms = useCallback(() => {
    setMinRoomsModalReason(null);
    dispatch({ type: "SET_ACCOMMODATIONS_NEEDED", payload: false });
    onNext();
  }, [dispatch, onNext]);

  // Effects
  // `accommodations.checkIn/checkOut` is only ever set FROM `formData.eventDates` (there's no
  // independent picker for it) — so if the planner goes back and edits the Dates step after
  // already saying "yes" to guestrooms here, this keeps the grid's nights in sync instead of
  // silently showing the stale range from whenever "Yes" was first clicked.
  useEffect(() => {
    if (!accommodations.needed) return;
    const preferredDates = asRange(formData.eventDates);
    if (!preferredDates) return;
    if (accommodations.checkIn === preferredDates.startDate && accommodations.checkOut === preferredDates.endDate) {
      return;
    }
    dispatch({
      type: "SET_ACCOMMODATION_DATES",
      payload: { checkIn: preferredDates.startDate, checkOut: preferredDates.endDate },
    });
  }, [accommodations.needed, accommodations.checkIn, accommodations.checkOut, formData.eventDates, dispatch]);

  // Render
  return (
    <div className="flex flex-col min-h-[420px]">
      <motion.div custom={0} variants={formElementVariants} initial="hidden" animate="visible">
        <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">Do you need guestrooms?</h1>
      </motion.div>

      <div className="flex-1 flex flex-col mt-10">
        <AnimatePresence mode="wait" custom={subDirection}>
          {subPhase === "gate" && (
            <SubPhasePanel phaseKey="gate" direction={subDirection}>
              <YesNoGate onYes={handleYes} onNo={handleNo} />
            </SubPhasePanel>
          )}

          {subPhase === "count" && (
            <SubPhasePanel phaseKey="count" direction={subDirection} className="flex flex-col gap-6">
              <p className="text-xl md:text-2xl font-semibold text-gray-900 tracking-tight">
                How many rooms do you need each night?
              </p>

              {hasDates ? (
                <GuestroomsGrid
                  nights={nights}
                  detailed={accommodationsDetailed ?? {}}
                  visibleColumns={visibleColumns}
                  availableTypesForAdd={availableTypesForAdd}
                  numPeople={formData.numPeople}
                  onChange={handleDetailChange}
                  onAddType={handleAddType}
                  onRemoveType={handleRemoveType}
                />
              ) : (
                <p className="text-xs font-medium text-gray-400">Pick your check-in/out dates first to see the grid.</p>
              )}
            </SubPhasePanel>
          )}
        </AnimatePresence>
      </div>

      <div className="flex gap-3 mt-8">
        {subPhase === "gate" ? (
          onBack && (
            <FetchButton variant="outline" color="neutral" size="lg" onClick={onBack} className="flex-1">
              Back
            </FetchButton>
          )
        ) : (
          <>
            <FetchButton variant="outline" color="neutral" size="lg" onClick={handleSubBack} className="flex-1">
              Back
            </FetchButton>
            <FetchButton
              variant="primary"
              size="lg"
              onClick={handleCountContinue}
              disabled={!hasDates || !hasCount}
              className="flex-1"
            >
              Continue
            </FetchButton>
          </>
        )}
      </div>

      <BelowMinRoomsModal
        isOpen={minRoomsModalReason !== null}
        onClose={() => setMinRoomsModalReason(null)}
        reason={minRoomsModalReason ?? "count"}
        onContinueWithoutGuestrooms={minRoomsModalReason === "count" ? handleContinueWithoutGuestrooms : undefined}
      />
    </div>
  );
};
